import {
  BarChartConfig,
  ChartTypeEnum,
  CombinationChartConfig,
  DonutChartConfig,
  ElementTypeEnum,
  GroupedColumnChartConfig,
  LineChartConfig,
  MostLeastEnum,
  PsDevicePageChartEnum,
  PsStorePageChartEnum,
  SummaryChartConfig,
  TableConfig,
} from 'shared/models/chart.models';
import { ApplicationConfigWithPages } from '../../../shared/features/ng-redux/app/appConfig.models';
import { PageConfigWithCharts, PageCategory } from '../../../shared/features/ng-redux/page/page.models';
import { CONSTANTS } from '../../../shared/localizations/dictionary';
import {
  CHART_UI_DONUT_LEAST_MOST,
  CHART_UI_DONUT_TOPN,
  CHART_UI_EXCLUDE_HOURS,
  PS_CHART_UI_USAGE_BY_STORE,
  PS_CHART_UI_USAGE_DATE_HOUR,
  PS_CHART_UI_USAGE_DEVICE_MAX_MIN,
  PS_CHART_UI_USAGE_WALL_MAX_MIN,
  PS_DEVICE_TABLE,
  PS_DEVICE_UTILIZATION_SUMMARY,
  UTILIZATION_DEVICE_PAGE,
  UTILIZATION_STORE_PAGE,
} from './app-mapper.constants';
import { buildChartOptions, patchChart, patchPage } from './app-mapper.utils';
import { HighchartsConstant } from '../../../../../../xs-colors';
import { InsSelectionType, TableRenderEnum } from '@nx-fe/table';

export function mapUtilizationDeviceConfig(app: ApplicationConfigWithPages): ApplicationConfigWithPages {
  patchPage(app, UTILIZATION_DEVICE_PAGE, patchUtilizationDevicePage);
  app.name = 'Analysis_Profile_Tab_Device_Page';
  app.dashboardType = CONSTANTS.DASHBOARD_TYPES.UTILIZATION;
  return app;
}

function patchUtilizationDevicePage(page: PageConfigWithCharts) {
  page.filterBarVisible = false;
  page.pageCategory = PageCategory.UTILIZATION;
  page.icon = 'insight-icon-utilization';
  page.dateFilterTypes = ['Custom', 'Today', 'Yesterday', 'Last Week', 'Last Month', 'Last 7 Days', 'Last 30 Days'];

  page.predefinedCharts = [
    {
      category: {
        name: 'Device_Charts_Category_Name_Caption',
        icon: 'insight-icon-device',
        tooltip: 'Device_Charts_Category_Name_Tooltip_Caption',
      },

      charts: [buildChartOptions(page.charts, PsDevicePageChartEnum.psdModel, 'predefined_psd_model_name')],
    },
    {
      category: {
        name: 'header_utilization_label',
        icon: 'insight-icon-device',
        tooltip: 'header_utilization_label_tooltip',
      },
      charts: [
        buildChartOptions(page.charts, PsDevicePageChartEnum.psdWallUsage, 'psd_predefined_peak_cradle_usage'),
        buildChartOptions(page.charts, PsDevicePageChartEnum.psdDeviceUsage, 'psd_predefined_device_usage'),
        buildChartOptions(page.charts, PsDevicePageChartEnum.psdAvgWallTime, 'psd_predefined_avg_time_cradle'),
        buildChartOptions(page.charts, PsDevicePageChartEnum.psdAvgUtilTime, 'psd_avg_device_util_name'),
        buildChartOptions(page.charts, PsDevicePageChartEnum.psdAvgDeviceReturn, 'psd_avg_device_return_cradle_name'),
      ],
    },
  ];

  patchChart(page.charts, PsDevicePageChartEnum.psdDevSummary, (chart: SummaryChartConfig) => {
    chart.elementType = ElementTypeEnum.SUMMARY;
    chart.name = 'devices_summary_name';
    chart.fullName = undefined;
    chart.layout = '33p';
    chart.fullName = 'devices_summary_full_name_dashboards';
    chart.items = PS_DEVICE_UTILIZATION_SUMMARY;
  });

  patchChart(page.charts, PsDevicePageChartEnum.psdModel, (chart: DonutChartConfig) => {
    chart.fullName = 'Model_reportsummary';
    chart.name = 'psd_model_name';
    chart.type = ChartTypeEnum.DONUT;
    chart.elementType = 'chart';
    chart.layout = '33p';
    chart.isFilterable = false;
    chart.allowPointSelect = false;

    chart.tooltip = {
      label: "`${xsI18nPipe.transform('data_chart_model_tooltip_label', this.point.name)}`",
      labelArgs: ['data_chart_model_tooltip_label', 'this.point.name'],
      value:
        "`${xsI18nPipe.transform('data_chart_models_tooltip_value', this.point.y,  (this.point.y/this.point.devcount*100).toFixed(2).replace(/\\.?0+$/,''), this.point.devcount)}`",
      valueArgs: [
        'data_chart_models_tooltip_value',
        'this.point.y',
        'percentage(this.point.y, this.point.devcount)',
        'this.point.devcount',
      ],
    };

    chart.tag = {
      name: 'psd_model_name',
      parameters: ['topN', 'mostLeast'],
    };
    chart.filter = {
      attribute: 'filter',
      type: 'mapped',
    };
    chart.filters = {
      topN: 5,
      mostLeast: MostLeastEnum.MOST,
    };
    chart.uiSettings = [CHART_UI_DONUT_TOPN, CHART_UI_DONUT_LEAST_MOST];
  });

  patchChart(page.charts, PsDevicePageChartEnum.psdWallUsage, (chart: LineChartConfig) => {
    chart.fullName = 'psd_peak_cradle_usage_fullname';
    chart.name = 'psd_peak_cradle_usage_name';
    chart.type = ChartTypeEnum.LINE;
    chart.elementType = 'chart';
    chart.layout = '33p';
    chart.integerOnly = true;
    chart.legendClick = true;
    chart.dimension = {
      dimensionId: 'x',
      label: 'xs_time_interval_label',
      noDataLabel: 'no_data_label',
    };
    chart.metric = {
      metricId: 'cnt',
      label: 'device_count_label',
    };
    chart.pointTimeId = 'pt';
    chart.tooltip = {
      label: 'xs_reported_date_label',
      value: 'psd_wall_usage_tooltip_value',
    };
    chart.dataMapId = 'wallMap';
    chart.dynamicLegend = true;
    chart.filters = {
      store: -1,
      showTop: 1,
      byDay: 0,
    };
    chart.uiSettings = [PS_CHART_UI_USAGE_BY_STORE, PS_CHART_UI_USAGE_WALL_MAX_MIN, PS_CHART_UI_USAGE_DATE_HOUR];
  });

  patchChart(page.charts, PsDevicePageChartEnum.psdDeviceUsage, (chart: GroupedColumnChartConfig) => {
    chart.fullName = 'psd_device_usage_fullname';
    chart.name = 'psd_device_usage_name';
    chart.type = ChartTypeEnum.GROUPED_COLUMN;
    chart.elementType = 'chart';
    chart.layout = '100p';
    chart.timePointId = 'timePoint';
    chart.integerOnly = true;
    chart.isFilterable = false;
    chart.allowPointSelect = false;
    chart.scrollableX = true;
    chart.activateScroll = 12; // for hour filter it will still show 24 columns without scroll. added a condition on the config mapper for hour filter
    chart.maxScrollView = 12;

    chart.dimension = {
      dimensionId: 'x',
      label: 'xs_time_interval_label',
      labelDay: 'xs_hours_of_day_label',
      noDataLabel: 'noData',
    };
    chart.metric = {
      metricId: 'metric',
      label: 'device_count_label',
    };
    chart.multiTooltip = [
      {
        id: 'y0',
        timePointMetricId: 'utilPoint',
        value: 'psd_device_usage_tooltip_value',
        metricName: 'psd_device_usage_metric_utilized',
        subId: 'y2',
      },
      {
        id: 'y1',
        timePointMetricId: 'notPoint',
        value: 'psd_device_usage_tooltip_value',
        metricName: 'psd_device_usage_metric_not_utilized',
        subId: 'y4',
      },
      {
        id: 'y3',
        timePointMetricId: 'offPoint',
        value: 'psd_device_usage_tooltip_value',
        metricName: 'psd_device_usage_metric_offline',
        subId: 'y5',
      },
    ];
    chart.metrics = [
      {
        id: 'y0',
        metricName: 'psd_device_usage_metric_utilized',
        type: 'bar',
        timePointMetricId: 'utilPoint',
      },
      {
        id: 'y1',
        metricName: 'psd_device_usage_metric_not_utilized',
        type: 'bar',
        timePointMetricId: 'notPoint',
      },
      {
        id: 'y3',
        metricName: 'psd_device_usage_metric_offline',
        type: 'bar',
        timePointMetricId: 'offPoint',
      },
    ];

    chart.fixedColors = [
      {
        label: 'psd_device_usage_metric_utilized',
        color: HighchartsConstant.UtilizationBucket.Utilized,
      },
      {
        label: 'psd_device_usage_metric_not_utilized',
        color: HighchartsConstant.UtilizationBucket.NotUtilized,
      },
      {
        label: 'psd_device_usage_metric_offline',
        color: HighchartsConstant.UtilizationBucket.Offline,
      },
    ];
    chart.filters = {
      showTop: 1,
      byDay: 0,
    };
    chart.uiSettings = [PS_CHART_UI_USAGE_DEVICE_MAX_MIN, PS_CHART_UI_USAGE_DATE_HOUR];
  });

  patchChart(page.charts, PsDevicePageChartEnum.psdAvgWallTime, (chart: BarChartConfig) => {
    chart.fullName = 'psd_avg_time_cradle_fullname';
    chart.name = 'psd_avg_time_cradle_name';
    chart.type = ChartTypeEnum.COLUMN;
    chart.elementType = 'chart';
    chart.layout = '33p';
    chart.isFilterable = false;
    chart.allowPointSelect = false;

    chart.dimension = {
      dimensionId: 'label',
      label: 'psd_avg_time_cradle_dimension_label',
      noDataLabel: 'no_data_label',
    };
    chart.metric = {
      metricId: 'y',
      label: 'minute_s_label',
    };
    chart.tooltip = {
      label: "`${xsI18nPipe.transform('psd_avg_wall_time_tooltip_label', this.point.label)}`",
      labelArgs: ['psd_avg_wall_time_tooltip_label', 'this.point.label'],
      value:
        "`${xsI18nPipe.transform('psd_avg_wall_time_tooltip_value', xsI18nPipe.transform(this.point.wallType), this.point.y}`",
      valueArgs: ['psd_avg_wall_time_tooltip_value', 'xsI18nPipe.transform(this.point.wallType)', 'this.point.y'],
    };
    chart.filters = {
      store: -1,
      excludeHours: true,
      exStart: '0:00',
      exEnd: '1:00',
    };
    chart.uiSettings = [PS_CHART_UI_USAGE_BY_STORE, CHART_UI_EXCLUDE_HOURS];
  });

  patchChart(page.charts, PsDevicePageChartEnum.psdAvgUtilTime, (chart: BarChartConfig) => {
    chart.fullName = 'psd_avg_device_util_fullname';
    chart.name = 'psd_avg_device_util_name';
    chart.type = ChartTypeEnum.COLUMN;
    chart.elementType = 'chart';
    chart.layout = '33p';
    chart.isFilterable = false;
    chart.allowPointSelect = false;
    chart.colors = ['#2E86C1'];
    chart.scrollableX = true;

    chart.dimension = {
      dimensionId: 'label',
      label: 'common_date_label',
      noDataLabel: 'no_data_label',
    };
    chart.metric = {
      metricId: 'y',
      label: 'minute_s_label',
    };
    chart.tooltip = {
      label: "`${xsI18nPipe.transform('psd_avg_device_return_tooltip_label', this.point.label)}`",
      labelArgs: ['psd_avg_device_return_tooltip_label', 'this.point.label'],
      value: "`${xsI18nPipe.transform('psd_avg_device_return_tooltip_value', this.point.y}`",
      valueArgs: ['psd_avg_device_return_tooltip_value', 'this.point.y'],
    };
  });

  patchChart(page.charts, PsDevicePageChartEnum.psdAvgDeviceReturn, (chart: BarChartConfig) => {
    chart.fullName = 'psd_avg_device_return_cradle_fullname';
    chart.name = 'psd_avg_device_return_cradle_name';
    chart.type = ChartTypeEnum.COLUMN;
    chart.elementType = 'chart';
    chart.layout = '33p';
    chart.isFilterable = false;
    chart.allowPointSelect = false;
    chart.colors = ['#2E86C1'];
    chart.scrollableX = true;

    chart.dimension = {
      dimensionId: 'label',
      label: 'common_date_label',
      noDataLabel: 'no_data_label',
    };
    chart.metric = {
      metricId: 'y',
      label: 'minute_s_label',
    };
    chart.tooltip = {
      label: "`${xsI18nPipe.transform('psd_avg_device_return_tooltip_label', this.point.label)}`",
      labelArgs: ['psd_avg_device_return_tooltip_label', 'this.point.label'],
      value: "`${xsI18nPipe.transform('psd_avg_device_return_tooltip_value', this.point.y}`",
      valueArgs: ['psd_avg_device_return_tooltip_value', 'this.point.y'],
    };
  });

  patchChart(page.charts, PS_DEVICE_TABLE, (chart: TableConfig) => {
    chart.name = 'Ticket_List_Devices_Header';
    chart.selectionType = InsSelectionType.multiple;
    chart.selectionText = 'device_selected_label';
    chart.selectionTextPlural = 'device_selected_label';
    chart.rowActions = [
      {
        action: 'customAttributes',
        tooltip: 'custom_attr_title',
        icon: 'insight-icon-deviceinfo',
      },
    ];
    chart.filters = {
      sortAttribute: 'McDeviceName',
      sortDirection: 'asc',
      pageSize: 50,
      pageSkip: 0,
    };
    chart.tableAttributes = [
      {
        id: 'McDeviceName',
        label: 'Column_Name_Device_Name',
        renderer: TableRenderEnum.STRING,
      },
      {
        id: 'McDeviceId',
        label: 'Column_Name_Device_Id',
        renderer: TableRenderEnum.STRING,
      },
      {
        id: 'Name',
        label: 'Column_Name_Device_Group',
        renderer: TableRenderEnum.STRING,
        iconClass: '',
      },
      {
        id: 'Path',
        label: 'group_path_label',
        renderer: TableRenderEnum.STRING,
        iconClass: '',
      },
      {
        id: 'SessionCnt',
        label: 'Column_Name_Session_Count',
        renderer: TableRenderEnum.NUMBER,
        rendererParams: { decimals: 0 },
      },
      {
        id: 'AvgSessionTimeStr',
        label: 'Column_Name_Average_Session_Time',
        renderer: TableRenderEnum.STRING,
        shouldTranslate: true,
      },
      {
        id: 'AvgEntranceTimeStr',
        label: 'Column_Name_Average_Entrance_Time',
        renderer: TableRenderEnum.STRING,
        shouldTranslate: true,
      },
      {
        id: 'AvgTransTimeStr',
        label: 'Column_Name_Average_Transfer_Time',
        renderer: TableRenderEnum.STRING,
        shouldTranslate: true,
      },
      {
        id: 'StoreName',
        label: 'Column_Name_Store_Name',
        renderer: TableRenderEnum.STRING,
        iconClass: '',
      },
      {
        id: 'OSVersion',
        label: 'Column_Name_Os_Version',
        renderer: TableRenderEnum.STRING,
        shouldTranslate: true,
      },
      {
        id: 'Manufacturer',
        label: 'Column_Name_Manufacturer',
        renderer: TableRenderEnum.STRING,
      },
      {
        id: 'Model',
        label: 'Column_Name_Model',
        renderer: TableRenderEnum.STRING,
      },
    ];
  });
}

export function mapUtilizationStoreConfig(app: ApplicationConfigWithPages): ApplicationConfigWithPages {
  patchPage(app, UTILIZATION_STORE_PAGE, patchUtilizationStorePage);
  app.name = 'Analysis_Profile_Tab_Store_page';
  app.dashboardType = CONSTANTS.DASHBOARD_TYPES.UTILIZATION;
  return app;
}

function patchUtilizationStorePage(page: PageConfigWithCharts) {
  page.filterBarVisible = false;
  page.pageCategory = PageCategory.UTILIZATION;
  page.icon = 'insight-icon-utilization-by-store';
  page.dateFilterTypes = ['Custom', 'Today', 'Yesterday', 'Last Week', 'Last Month', 'Last 7 Days', 'Last 30 Days'];

  page.predefinedCharts = [
    {
      category: {
        name: 'pss_chart_category_info_name',
        icon: 'insight-icon-device',
        tooltip: 'pss_chart_category_info_name_tooltip',
      },
      charts: [
        buildChartOptions(
          page.charts,
          PsStorePageChartEnum.pssPeakDevices,
          'predefined_pss_device_util_store_devices_name'
        ),
        buildChartOptions(page.charts, PsStorePageChartEnum.pssPeakPct, 'predefined_pss_device_util_store_perc_name'),
        buildChartOptions(
          page.charts,
          PsStorePageChartEnum.pssLeastDevices,
          'predefined_pss_least_utilized_devices_name'
        ),
        buildChartOptions(
          page.charts,
          PsStorePageChartEnum.pssLeastPct,
          'predefined_pss_least_utilized_devices_perc_name'
        ),
        buildChartOptions(
          page.charts,
          PsStorePageChartEnum.pssAvgUtil,
          'pss_avg_device_util_name'
        ),
        buildChartOptions(
          page.charts,
          PsStorePageChartEnum.pssAvgReturn,
          'predefined_pss_avg_device_return_store_name'
        ),
      ],
    },
  ];

  patchChart(page.charts, PsStorePageChartEnum.pssPeakDevices, (chart: GroupedColumnChartConfig) => {
    chart.fullName = 'pss_device_util_store_devices_fullname';
    chart.name = 'pss_device_util_store_devices_name';
    chart.type = ChartTypeEnum.GROUPED_COLUMN;
    chart.elementType = 'chart';
    chart.layout = '50p';
    chart.isFilterable = false;
    chart.allowPointSelect = false;
    chart.scrollableX = true;
    chart.scrollableX = true;
    chart.activateScroll = 6;
    chart.maxScrollView = 6;

    chart.integerOnly = true;
    chart.dimension = {
      dimensionId: 'StoreName',
      label: 'pss_avg_device_return_store_dimension_label',
      noDataLabel: 'no_data_label',
    };
    chart.metric = {
      metricId: null,
      label: 'device_count_label',
    };
    chart.tooltip = {
      label: 'pss_device_usage_tooltip_label',
      value: 'pss_device_usage_tooltip_value',
    };

    chart.timePointId = 'timePoint';
    chart.metrics = [
      {
        id: 'maxUtil',
        metricName: 'psd_device_usage_metric_utilized',
        type: 'bar',
        timePointMetricId: 'maxUtilPoint',
      },
      {
        id: 'maxNot',
        metricName: 'psd_device_usage_metric_not_utilized',
        type: 'bar',
        timePointMetricId: 'maxNotPoint',
      },
      {
        id: 'maxOff',
        metricName: 'psd_device_usage_metric_offline',
        type: 'bar',
        timePointMetricId: 'maxOffPoint',
      },
    ];

    chart.fixedColors = [
      {
        label: 'psd_device_usage_metric_utilized',
        color: HighchartsConstant.UtilizationBucket.Utilized,
      },
      {
        label: 'psd_device_usage_metric_not_utilized',
        color: HighchartsConstant.UtilizationBucket.NotUtilized,
      },
      {
        label: 'psd_device_usage_metric_offline',
        color: HighchartsConstant.UtilizationBucket.Offline,
      },
    ];
  });

  patchChart(page.charts, PsStorePageChartEnum.pssPeakPct, (chart: GroupedColumnChartConfig) => {
    chart.fullName = 'pss_device_util_store_perc_fullname';
    chart.name = 'pss_device_util_store_perc_name';
    chart.type = ChartTypeEnum.GROUPED_COLUMN;
    chart.elementType = 'chart';
    chart.layout = '50p';
    chart.isFilterable = false;
    chart.allowPointSelect = false;
    chart.scrollableX = true;
    chart.activateScroll = 6;
    chart.maxScrollView = 6;

    chart.yMax = 100;
    chart.integerOnly = true;
    chart.dimension = {
      dimensionId: 'StoreName',
      label: 'pss_avg_device_return_store_dimension_label',
      noDataLabel: 'no_data_label',
    };
    chart.metric = {
      metricId: null,
      label: 'pss_percentage_devices_metric_label',
    };
    chart.tooltip = {
      label: 'pss_device_usage_tooltip_label',
      value: 'pss_percentage_devices_tooltip_value',
    };

    chart.timePointId = 'timePoint';
    chart.metrics = [
      {
        id: 'pctMaxUtil',
        metricName: 'psd_device_usage_metric_utilized',
        type: 'bar',
        timePointMetricId: 'maxUtilPoint',
      },
      {
        id: 'pctMaxNot',
        metricName: 'psd_device_usage_metric_not_utilized',
        type: 'bar',
        timePointMetricId: 'maxNotPoint',
      },
      {
        id: 'pctMaxOff',
        metricName: 'psd_device_usage_metric_offline',
        type: 'bar',
        timePointMetricId: 'maxOffPoint',
      },
    ];

    chart.fixedColors = [
      {
        label: 'psd_device_usage_metric_utilized',
        color: HighchartsConstant.UtilizationBucket.Utilized,
      },
      {
        label: 'psd_device_usage_metric_not_utilized',
        color: HighchartsConstant.UtilizationBucket.NotUtilized,
      },
      {
        label: 'psd_device_usage_metric_offline',
        color: HighchartsConstant.UtilizationBucket.Offline,
      },
    ];
  });

  patchChart(page.charts, PsStorePageChartEnum.pssLeastDevices, (chart: GroupedColumnChartConfig) => {
    chart.fullName = 'pss_least_utilized_devices_fullname';
    chart.name = 'pss_least_utilized_devices_name';
    chart.type = ChartTypeEnum.GROUPED_COLUMN;
    chart.elementType = 'chart';
    chart.layout = '50p';
    chart.isFilterable = false;
    chart.allowPointSelect = false;
    chart.scrollableX = true;
    chart.activateScroll = 6;
    chart.maxScrollView = 6;

    chart.integerOnly = true;
    chart.dimension = {
      dimensionId: 'StoreName',
      label: 'pss_avg_device_return_store_dimension_label',
      noDataLabel: 'no_data_label',
    };
    chart.metric = {
      metricId: null,
      label: 'device_count_label',
    };
    chart.tooltip = {
      label: 'pss_device_usage_tooltip_label',
      value: 'pss_device_usage_tooltip_value',
    };

    chart.timePointId = 'timePoint';
    chart.metrics = [
      {
        id: 'minUtil',
        metricName: 'psd_device_usage_metric_utilized',
        type: 'bar',
        timePointMetricId: 'minUtilPoint',
      },
      {
        id: 'minNot',
        metricName: 'psd_device_usage_metric_not_utilized',
        type: 'bar',
        timePointMetricId: 'minNotPoint',
      },
      {
        id: 'minOff',
        metricName: 'psd_device_usage_metric_offline',
        type: 'bar',
        timePointMetricId: 'minOffPoint',
      },
    ];

    chart.fixedColors = [
      {
        label: 'psd_device_usage_metric_utilized',
        color: HighchartsConstant.UtilizationBucket.Utilized,
      },
      {
        label: 'psd_device_usage_metric_not_utilized',
        color: HighchartsConstant.UtilizationBucket.NotUtilized,
      },
      {
        label: 'psd_device_usage_metric_offline',
        color: HighchartsConstant.UtilizationBucket.Offline,
      },
    ];
  });

  patchChart(page.charts, PsStorePageChartEnum.pssLeastPct, (chart: GroupedColumnChartConfig) => {
    chart.fullName = 'pss_least_utilized_devices_perc_fullname';
    chart.name = 'pss_least_utilized_devices_perc_name';
    chart.type = ChartTypeEnum.GROUPED_COLUMN;
    chart.elementType = 'chart';
    chart.layout = '50p';
    chart.isFilterable = false;
    chart.allowPointSelect = false;
    chart.scrollableX = true;
    chart.activateScroll = 6;
    chart.maxScrollView = 6;

    chart.yMax = 100;
    chart.integerOnly = true;
    chart.dimension = {
      dimensionId: 'StoreName',
      label: 'pss_avg_device_return_store_dimension_label',
      noDataLabel: 'no_data_label',
    };
    chart.metric = {
      metricId: null,
      label: 'pss_percentage_devices_metric_label',
    };
    chart.tooltip = {
      label: 'pss_device_usage_tooltip_label',
      value: 'pss_percentage_devices_tooltip_value',
    };

    chart.timePointId = 'timePoint';
    chart.metrics = [
      {
        id: 'pctMinUtil',
        metricName: 'psd_device_usage_metric_utilized',
        type: 'bar',
        timePointMetricId: 'minUtilPoint',
      },
      {
        id: 'pctMinNot',
        metricName: 'psd_device_usage_metric_not_utilized',
        type: 'bar',
        timePointMetricId: 'minNotPoint',
      },
      {
        id: 'pctMinOff',
        metricName: 'psd_device_usage_metric_offline',
        type: 'bar',
        timePointMetricId: 'minOffPoint',
      },
    ];

    chart.fixedColors = [
      {
        label: 'psd_device_usage_metric_utilized',
        color: HighchartsConstant.UtilizationBucket.Utilized,
      },
      {
        label: 'psd_device_usage_metric_not_utilized',
        color: HighchartsConstant.UtilizationBucket.NotUtilized,
      },
      {
        label: 'psd_device_usage_metric_offline',
        color: HighchartsConstant.UtilizationBucket.Offline,
      },
    ];
  });

  patchChart(page.charts, PsStorePageChartEnum.pssAvgUtil, (chart: BarChartConfig) => {
    chart.fullName = 'pss_avg_device_util_fullname';
    chart.name = 'pss_avg_device_util_name';
    chart.type = ChartTypeEnum.COLUMN;
    chart.elementType = 'chart';
    chart.layout = '50p';
    chart.isFilterable = false;
    chart.allowPointSelect = false;
    chart.scrollableX = true;

    chart.dimension = {
      dimensionId: 'name',
      label: 'pss_avg_device_return_store_dimension_label',
      noDataLabel: 'no_data_label',
    };
    chart.metric = {
      metricId: 'y',
      label: 'pss_avg_device_return_store_metric_label',
    };
    chart.tooltip = {
      label: "`${xsI18nPipe.transform('pss_avg_wall_time_tooltip_label', this.point.name)}`",
      labelArgs: ['pss_avg_wall_time_tooltip_label', 'this.point.name'],
      value: "`${xsI18nPipe.transform('pss_avg_wall_time_tooltip_value', this.point.y}`",
      valueArgs: ['pss_avg_wall_time_tooltip_value', 'this.point.y'],
    };
  });

  patchChart(page.charts, PsStorePageChartEnum.pssAvgReturn, (chart: BarChartConfig) => {
    chart.fullName = 'pss_avg_device_return_store_fullname';
    chart.name = 'pss_avg_device_return_store_name';
    chart.type = ChartTypeEnum.COLUMN;
    chart.elementType = 'chart';
    chart.layout = '50p';
    chart.isFilterable = false;
    chart.allowPointSelect = false;
    chart.scrollableX = true;

    chart.dimension = {
      dimensionId: 'name',
      label: 'pss_avg_device_return_store_dimension_label',
      noDataLabel: 'no_data_label',
    };
    chart.metric = {
      metricId: 'y',
      label: 'pss_avg_device_return_store_metric_label',
    };
    chart.tooltip = {
      label: "`${xsI18nPipe.transform('pss_avg_wall_time_tooltip_label', this.point.name)}`",
      labelArgs: ['pss_avg_wall_time_tooltip_label', 'this.point.name'],
      value: "`${xsI18nPipe.transform('pss_avg_wall_time_tooltip_value', this.point.y}`",
      valueArgs: ['pss_avg_wall_time_tooltip_value', 'this.point.y'],
    };
  });
}
